import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import {
  ClassificationFlowBatchCard,
  ClassificationFlowCallTime,
  ClassificationFlowBatchCallDetail,
  ClassificationFlowBatchDetail,
  ClassificationFlowBatchItemDetail,
  ClassificationFlowBatchStep,
  ClassificationFlowBatchSummary,
  ClassificationFlowEvent,
  ClassificationFlowVoteApproval,
  ClassificationFlowVoteRow,
  ClassificationModelInfo,
  ClassificationPropose,
  CategoryCode,
  TypeCode
} from '@core/models/models';

type FlowTab = 'single' | 'scoreboard' | 'unlabeled';
type BatchFilter = 'all' | 'saved' | 'blank' | 'error';

/** One shelf level shown on a result: department, group, or sub-group. */
interface TaxonomyLevel {
  label: string;
  name: string;
  code: string | null;
}

/** One plan-level phase with optional nested sub-steps (e.g. Category / Type under a model). */
interface FlowPhase {
  id: string;
  title: string;
  subtitle: string;
  status: string;
  decision?: string | null;
  why?: string | null;
  model?: string | null;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  event: ClassificationFlowEvent | null;
  children: ClassificationFlowEvent[];
}

export interface ClassificationFlowCallSegment {
  call: ClassificationFlowCallTime;
  step: string;
  isAisle: boolean;
  notSent: boolean;
  sentClock: string | null;
  receivedClock: string | null;
  answered: boolean;
}

@Component({
  selector: 'app-classification-flow-admin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './classification-flow-admin.component.html',
  styleUrl: './classification-flow-admin.component.css'
})
export class ClassificationFlowAdminComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private abort: AbortController | null = null;
  private batchTimer: ReturnType<typeof setInterval> | null = null;

  tab: FlowTab = 'single';
  barcode = '';
  scoreboardLimit = 20;
  /** When true (default), do not send product photos to LLMs. */
  skipPhotos = true;

  running = false;
  picking: 'gold' | 'plain' | null = null;
  errorMessage = '';
  statusMessage = '';

  steps: ClassificationFlowEvent[] = [];
  score: ClassificationFlowEvent | null = null;
  productId: number | null = null;
  productLabel = '';
  hasImage = false;
  brandName = '';
  qtyLabel = '';
  imagePopupOpen = false;
  imagePopupProductId: number | null = null;
  private imagePopupCloseTimer: ReturnType<typeof setTimeout> | null = null;

  scoreboardRows: ClassificationFlowEvent[] = [];
  scoreboardSummary: ClassificationFlowEvent | null = null;
  /** Empty means every available voting company. A model id scores that model alone. */
  scoreboardModel = '';
  scoreboardModels: ClassificationModelInfo[] = [];
  batchLimit = 50;
  batchApi = true;
  batchStarting = false;
  batches: ClassificationFlowBatchSummary[] = [];
  batch: ClassificationFlowBatchDetail | null = null;
  batchFilter: BatchFilter = 'all';
  openProductId: number | null = null;
  batchDetail: ClassificationFlowBatchItemDetail | null = null;
  batchDetailLoading = false;
  approvingKey: string | null = null;
  rerunningProductId: number | null = null;

  callIoPopup: {
    batchId: number;
    customId: string;
    model: string;
    modelName: string;
    wave: string;
    stepTitle: string;
    productName: string;
    barcode: string | null;
    sentAt: string | null;
    receivedAt: string | null;
    prompt: string | null;
    response: string | null;
    error: string | null;
    answered: boolean;
  } | null = null;
  callIoLoading = false;
  callIoTab: 'prompt' | 'output' = 'prompt';
  callIoCopied = false;
  private categoryBySlug = new Map<string, CategoryCode>();
  private typeBySlug = new Map<string, TypeCode>();

  /** Keys of expanded inline input/output panels (no popups). */
  expandedIo: Record<string, boolean> = { score: false };

  ngOnInit(): void {
    void this.loadCodes();
  }

  ngOnDestroy(): void {
    this.abort?.abort();
    this.cancelCloseImagePopup();
    this.stopBatchPoll();
  }

  /** Group flat SSE steps into plan phases with nested Category/Type under each model. */
  get phases(): FlowPhase[] {
    const order: string[] = [];
    const map = new Map<string, FlowPhase>();

    const ensure = (id: string, title: string, subtitle: string): FlowPhase => {
      let phase = map.get(id);
      if (!phase) {
        phase = {
          id,
          title,
          subtitle,
          status: 'pending',
          tokensIn: 0,
          tokensOut: 0,
          costUsd: 0,
          event: null,
          children: []
        };
        map.set(id, phase);
        order.push(id);
      }
      return phase;
    };

    for (const evt of this.steps) {
      const step = evt.step;

      if (step === 'lookup' || step === 'evidence') {
        const phase = ensure(
          'evidence',
          'ProductEvidence',
          'Collect display name, raw name, storefront titles, aisle priors, ingredients, and photo.'
        );
        this.applyParent(phase, evt);
        continue;
      }

      if (step === 'cheap' || step === 'cheap_check') {
        const phase = ensure(
          'cheap',
          'CheapSignals',
          'Deterministic priors (phrase / neighbor / aisle). High agreement can skip or shrink LLM work.'
        );
        this.applyParent(phase, evt);
        continue;
      }

      if (step === 'route') {
        const phase = ensure(
          'route',
          'CalibratedRoute',
          'One model per company. Save when two agree.'
        );
        this.applyParent(phase, evt);
        continue;
      }

      if (step === 'category_route') {
        const phase = ensure(
          'category_route',
          'Group route',
          'Group step is always evaluated by LLM; storefront aisle crosswalk groups are added as candidates in the sub-group step.'
        );
        this.applyParent(phase, evt);
        continue;
      }

      if (step === 'llm_consensus') {
        const phase = ensure(
          'gate',
          'AgreementAndRiskGate',
          'Save when two companies pick the same existing group and sub-group. An error or abstention does not veto.'
        );
        this.applyParent(phase, evt);
        continue;
      }

      if (step === 'save') {
        const phase = ensure(
          'save',
          'BackendPolicyGate',
          'Shadow only: Save or Leave blank. No product_classification write.'
        );
        this.applyParent(phase, evt);
        continue;
      }

      if (step === 'group' || step === 'category' || step === 'sub_group' || step === 'type' || step === 'shelf_match') {
        const modelId = evt.model || 'unknown';
        const phase = ensure(
          `model:${modelId}`,
          modelId,
          'Group, then sub-group, from the closed catalog.'
        );
        this.upsertChild(phase, evt);
        phase.status = this.rollStatus(phase);
        phase.tokensIn = phase.children.reduce((a, c) => a + (c.tokensIn || 0), 0);
        phase.tokensOut = phase.children.reduce((a, c) => a + (c.tokensOut || 0), 0);
        phase.costUsd = phase.children.reduce((a, c) => a + (c.costUsd || 0), 0);
        if ((step === 'sub_group' || step === 'type') && evt.status === 'done') {
          phase.decision =
            `${evt.predictedCategory ?? '—'}/${evt.predictedType ?? evt.decision ?? '—'}`;
        }
        continue;
      }

      if (step.startsWith('model:')) {
        const modelId = step.slice(6);
        const phase = ensure(
          `model:${modelId}`,
          modelId,
          'Group, then sub-group, from the closed catalog.'
        );
        phase.model = modelId;
        // Parent envelope: keep running/error/done without replacing Category/Type children.
        if (evt.status === 'running' && phase.status === 'pending') {
          phase.status = 'running';
        } else if (evt.status === 'error') {
          phase.status = 'error';
          phase.why = evt.why;
          phase.decision = evt.decision;
        } else if (evt.status === 'done') {
          phase.status = phase.children.length > 0 ? this.rollStatus(phase) : 'done';
          phase.decision = evt.decision ?? phase.decision;
          phase.why = evt.why ?? phase.why;
          if (phase.children.length === 0) {
            phase.tokensIn = evt.tokensIn;
            phase.tokensOut = evt.tokensOut;
            phase.costUsd = evt.costUsd;
            phase.event = evt;
          }
        }
        continue;
      }

      const phase = ensure(
        step,
        this.stepTitle(step),
        this.stepSubtitle(step)
      );
      this.applyParent(phase, evt);
    }

    const built = order.map((id) => map.get(id)!);
    return built;
  }

  setTab(tab: FlowTab): void {
    if (this.running) {
      return;
    }
    this.tab = tab;
    this.errorMessage = '';
    this.statusMessage = '';
    if (tab === 'unlabeled') {
      void this.loadBatches();
    }
    if (tab === 'scoreboard') {
      void this.loadScoreboardModels();
    }
  }

  get scoreboardChoices(): ClassificationModelInfo[] {
    const choices = this.scoreboardModels.filter((m) => m.available);
    return choices.sort((a, b) => {
      if (a.id === 'claude-haiku-4-5') return -1;
      if (b.id === 'claude-haiku-4-5') return 1;
      return 0;
    });
  }

  private async loadScoreboardModels(): Promise<void> {
    try {
      const res = await firstValueFrom(this.api.listClassificationModels());
      this.scoreboardModels = res.models || [];
      if (
        this.scoreboardModel &&
        !this.scoreboardChoices.some((m) => m.id === this.scoreboardModel)
      ) {
        this.scoreboardModel = '';
      }
    } catch {
      this.scoreboardModels = [];
    }
  }

  ioKey(evt: ClassificationFlowEvent): string {
    return `${evt.step}|${evt.model ?? ''}`;
  }

  phaseIoKey(phase: FlowPhase): string {
    return `phase|${phase.id}`;
  }

  isIoOpen(key: string): boolean {
    return this.expandedIo[key] !== false;
  }

  toggleIo(key: string): void {
    this.expandedIo = { ...this.expandedIo, [key]: !this.isIoOpen(key) };
  }

  async pickSample(hasGold: boolean): Promise<void> {
    this.picking = hasGold ? 'gold' : 'plain';
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      const sample = await firstValueFrom(this.api.randomClassificationFlowSample(hasGold));
      const code = (sample.barcode || '').trim();
      if (!code) {
        this.errorMessage = 'That product has no barcode.';
        return;
      }
      this.barcode = code;
      this.productId = sample.productId;
      this.productLabel = sample.displayName;
      this.steps = [];
      this.score = null;
      this.hasImage = false;
      this.brandName = '';
      this.qtyLabel = '';
      this.statusMessage = hasGold
        ? `Gold label ${sample.goldCategory}/${sample.goldType}. Press Generate.`
        : 'No gold label. Press Generate.';
    } catch (err) {
      const http = err as HttpErrorResponse;
      const body = http?.error as { error?: string } | undefined;
      this.errorMessage = body?.error || 'Could not pick a product.';
    } finally {
      this.picking = null;
    }
  }

  async generate(): Promise<void> {
    const code = this.barcode.trim();
    if (!code) {
      this.errorMessage = 'Enter a barcode.';
      return;
    }

    this.abort?.abort();
    this.abort = new AbortController();
    this.running = true;
    this.errorMessage = '';
    this.statusMessage = 'Running… (shadow — no DB write)';
    this.steps = [];
    this.score = null;
    this.productId = null;
    this.productLabel = '';
    this.hasImage = false;
    this.brandName = '';
    this.qtyLabel = '';
    this.imagePopupOpen = false;
    this.expandedIo = { score: false };

    try {
      for await (const evt of this.api.streamClassificationFlow(code, {
        includePhoto: !this.skipPhotos,
        signal: this.abort.signal
      })) {
        if (evt.barcode) {
          this.barcode = evt.barcode;
        }
        if (evt.productId) {
          this.productId = evt.productId;
        }
        if (evt.displayName) {
          this.productLabel = evt.displayName;
        }
        if (evt.hasImage != null) {
          this.hasImage = evt.hasImage;
        }
        if (evt.brandName) {
          this.brandName = evt.brandName;
        }
        if (evt.qtyLabel) {
          this.qtyLabel = evt.qtyLabel;
        }

        if (evt.event === 'error') {
          this.errorMessage = evt.why || 'Run failed.';
          this.upsertStep(evt);
          break;
        }

        if (evt.event === 'score') {
          this.score = evt;
          continue;
        }

        if (evt.event === 'step') {
          this.upsertStep(evt);
        }
      }

      if (!this.errorMessage) {
        this.statusMessage = this.score
          ? `Done · ${this.score.decision ?? 'score'}`
          : 'Done';
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        this.statusMessage = 'Cancelled.';
      } else {
        this.errorMessage = err instanceof Error ? err.message : 'Run failed.';
      }
    } finally {
      this.running = false;
      this.abort = null;
    }
  }

  async runScoreboard(): Promise<void> {
    this.abort?.abort();
    this.abort = new AbortController();
    this.running = true;
    this.errorMessage = '';
    this.statusMessage = 'Running scoreboard on gold (admin-verified) products…';
    this.scoreboardRows = [];
    this.scoreboardSummary = null;

    try {
      for await (const evt of this.api.streamClassificationScoreboard(this.scoreboardLimit, {
        includePhoto: !this.skipPhotos,
        model: this.scoreboardModel,
        signal: this.abort.signal
      })) {
        if (evt.event === 'scoreboard_start') {
          this.statusMessage = evt.why || this.statusMessage;
          continue;
        }
        if (evt.event === 'scoreboard_row' || evt.event === 'score') {
          this.scoreboardRows = [...this.scoreboardRows, evt];
          continue;
        }
        if (evt.event === 'scoreboard_summary') {
          this.scoreboardSummary = evt;
          this.statusMessage = evt.why || 'Scoreboard done';
        }
        if (evt.event === 'error') {
          this.errorMessage = evt.why || 'Scoreboard error.';
        }
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        this.statusMessage = 'Cancelled.';
      } else {
        this.errorMessage = err instanceof Error ? err.message : 'Scoreboard failed.';
      }
    } finally {
      this.running = false;
      this.abort = null;
    }
  }

  cancel(): void {
    this.abort?.abort();
  }

  subStepTitle(step: string): string {
    if (step === 'group' || step === 'category') return 'Group';
    if (step === 'sub_group' || step === 'type') return 'Sub-group';
    if (step === 'shelf_match') return 'Name match';
    return this.stepTitle(step);
  }

  subStepSubtitle(step: string): string {
    if (step === 'group' || step === 'category') {
      return 'Choose an existing group (or none/abstain). Prefer the aisle when the cues agree.';
    }
    if (step === 'sub_group' || step === 'type') {
      return 'From the chosen group, the alternate, or a name-matched group. Not flavour or brand.';
    }
    if (step === 'shelf_match') {
      return 'Sub-groups whose names match the product. Opens their groups for this step.';
    }
    return this.stepSubtitle(step);
  }

  /** Labels match Classification Architecture Plan node names. */
  stepTitle(step: string): string {
    if (step === 'lookup') return 'ValidateAndNormalize';
    if (step === 'evidence') return 'ProductEvidence';
    if (step === 'cheap' || step === 'cheap_check') return 'CheapSignals';
    if (step === 'route') return 'CalibratedRoute';
    if (step === 'category_route') return 'Group route';
    if (step === 'llm_consensus') return 'AgreementAndRiskGate';
    if (step === 'save') return 'BackendPolicyGate (shadow)';
    if (step === 'score') return 'Score';
    if (step === 'scoreboard') return 'Scoreboard';
    if (step.startsWith('model:')) return step.slice(6);
    if (step === 'group' || step === 'category') return 'Group';
    if (step === 'sub_group' || step === 'type') return 'Sub-group';
    if (step === 'shelf_match') return 'Name match';
    return step;
  }

  stepSubtitle(step: string): string {
    if (step === 'lookup' || step === 'evidence') {
      return 'Collect display name, raw name, storefront titles, aisle priors, ingredients, and photo.';
    }
    if (step === 'cheap' || step === 'cheap_check') {
      return 'Deterministic priors (phrase / neighbor / aisle). High agreement can skip or shrink LLM work.';
    }
    if (step === 'route') {
      return 'One model per company: Gemini 2.5 Pro, GPT-5 Mini, Claude Haiku.';
    }
    if (step === 'category_route') {
      return 'Group step is always evaluated by LLM; storefront aisle crosswalk groups are added as candidates in the sub-group step.';
    }
    if (step === 'llm_consensus') {
      return 'Save when two companies pick the same existing group and sub-group. An error or abstention does not veto.';
    }
    if (step === 'save') {
      return 'Shadow only: Save or Leave blank. No DB write.';
    }
    if (step === 'score') {
      return 'Compare the predicted group and sub-group to admin gold when verified.';
    }
    if (step === 'shelf_match') {
      return 'Sub-groups whose names match the product. Opens their groups for the sub-group step.';
    }
    return 'Classification Architecture Plan step.';
  }

  /** Readable IO panels: expand multiline strings, split system/user prompts. */
  prettyIo(value: unknown): { label: string | null; kind: 'prompt' | 'json'; text: string }[] {
    if (value == null) {
      return [];
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          const parsed = JSON.parse(trimmed) as unknown;
          return [{ label: null, kind: 'json', text: this.formatReadable(parsed) }];
        } catch {
          // not valid JSON, treat as prompt
        }
      }
      return this.blocksForPromptString(null, value);
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;
      const promptBlocks: { label: string | null; kind: 'prompt' | 'json'; text: string }[] = [];
      const rest: Record<string, unknown> = {};

      for (const [key, raw] of Object.entries(obj)) {
        if (typeof raw === 'string' && this.looksLikePromptField(key, raw)) {
          promptBlocks.push(...this.blocksForPromptString(key, raw));
        } else {
          rest[key] = raw;
        }
      }

      const blocks = [...promptBlocks];
      if (Object.keys(rest).length > 0) {
        blocks.push({
          label: promptBlocks.length > 0 ? 'Other fields' : null,
          kind: 'json',
          text: this.formatReadable(rest)
        });
      }
      return blocks.length > 0 ? blocks : [{ label: null, kind: 'json', text: this.formatReadable(value) }];
    }

    return [{ label: null, kind: 'json', text: this.formatReadable(value) }];
  }

  formatJson(value: unknown): string {
    return this.formatReadable(value);
  }

  private looksLikePromptField(key: string, value: string): boolean {
    if (/llm(input|output)|prompt|system|user/i.test(key)) {
      return true;
    }
    return value.includes('\n') && value.length > 80;
  }

  private blocksForPromptString(
    label: string | null,
    raw: string
  ): { label: string | null; kind: 'prompt' | 'json'; text: string }[] {
    const text = this.normalizeNewlines(raw);
    const roles = this.splitPromptRoles(text);
    if (roles.length > 1) {
      return roles.map((role) => ({
        label: label ? `${label} · ${role.role}` : role.role,
        kind: 'prompt' as const,
        text: this.prettifyPromptBody(role.body)
      }));
    }
    return [
      {
        label,
        kind: 'prompt',
        text: this.prettifyPromptBody(text)
      }
    ];
  }

  private splitPromptRoles(text: string): { role: string; body: string }[] {
    const re = /^(system|user|assistant)\s*:\s*\n?/gim;
    const matches = [...text.matchAll(re)];
    if (matches.length === 0) {
      return [{ role: 'prompt', body: text }];
    }
    const parts: { role: string; body: string }[] = [];
    for (let i = 0; i < matches.length; i++) {
      const m = matches[i];
      const start = (m.index ?? 0) + m[0].length;
      const end = i + 1 < matches.length ? (matches[i + 1].index ?? text.length) : text.length;
      parts.push({
        role: m[1].toLowerCase(),
        body: text.slice(start, end).trim()
      });
    }
    return parts;
  }

  /** Break dense "a (x), b (y), …" option lists onto separate lines. */
  private prettifyPromptBody(body: string): string {
    return body
      .split('\n')
      .map((line) => {
        const trimmed = line.trimEnd();
        const listMatch = /^(Existing (?:categories|types)|Categories|Types)\s*:\s*(.+)$/i.exec(
          trimmed
        );
        if (!listMatch) {
          return trimmed;
        }
        const items = listMatch[2]
          .split(/\s*,\s*/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (items.length < 3) {
          return trimmed;
        }
        return `${listMatch[1]}:\n${items.map((item) => `  - ${item}`).join('\n')}`;
      })
      .join('\n');
  }

  private normalizeNewlines(value: string): string {
    return value.replace(/\r\n/g, '\n').replace(/\\n/g, '\n');
  }

  /** Pretty-print JSON-like values; multiline strings expand instead of \n escapes. */
  private formatReadable(value: unknown, indent = 0, key?: string): string {
    const pad = '  '.repeat(indent);
    const padInner = '  '.repeat(indent + 1);

    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'string') {
      const normalized = this.withCatalogName(key, this.normalizeNewlines(value));
      if (normalized.includes('\n') || normalized.length > 100) {
        return (
          '|\n' +
          normalized
            .split('\n')
            .map((line) => padInner + line)
            .join('\n')
        );
      }
      return JSON.stringify(normalized);
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '[]';
      }
      const items = value.map((item) => {
        const rendered = this.formatReadable(item, indent + 1, key);
        return rendered.startsWith('|')
          ? `${padInner}${rendered}`
          : `${padInner}${rendered}`;
      });
      return `[\n${items.join(',\n')}\n${pad}]`;
    }
    if (typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>);
      if (entries.length === 0) {
        return '{}';
      }
      const lines = entries.map(([entryKey, raw]) => {
        const rendered = this.formatReadable(raw, indent + 1, entryKey);
        if (rendered.startsWith('|')) {
          return `${padInner}${entryKey}:${rendered}`;
        }
        return `${padInner}${entryKey}: ${rendered}`;
      });
      return `{\n${lines.join('\n')}\n${pad}}`;
    }
    return String(value);
  }

  /** Per-model rows from score.output.votes for the comparison table. */
  scoreVotes(score: ClassificationFlowEvent | null): ClassificationFlowVoteRow[] {
    if (!score?.output || typeof score.output !== 'object') {
      return [];
    }
    const votes = (score.output as { votes?: unknown }).votes;
    if (!Array.isArray(votes)) {
      return [];
    }
    return votes
      .map((raw) => this.normalizeVoteRow(raw))
      .filter((v): v is ClassificationFlowVoteRow => v != null);
  }

  /** Department, group, and sub-group as separate labeled lines. */
  taxonomyLevels(
    categorySlug: string | null | undefined,
    typeSlug: string | null | undefined
  ): TaxonomyLevel[] {
    const levels: TaxonomyLevel[] = [];
    const group = this.groupParts(categorySlug);
    if (group?.department) {
      levels.push({ label: 'Department', name: group.department, code: null });
    }
    if (group) {
      levels.push({
        label: 'Group',
        name: group.name || group.slug,
        code: group.name ? group.slug : null
      });
    }
    const sub = this.subGroupParts(typeSlug);
    if (sub) {
      levels.push({
        label: 'Sub-group',
        name: sub.name || sub.slug,
        code: sub.name ? sub.slug : null
      });
    }
    return levels;
  }

  voteLevels(v: ClassificationFlowVoteRow): TaxonomyLevel[] {
    const levels: TaxonomyLevel[] = [];
    const group = this.groupParts(v.categorySlug);
    if (group?.department) {
      levels.push({ label: 'Department', name: group.department, code: null });
    }
    if (group) {
      levels.push({
        label: 'Group',
        name: group.name || group.slug,
        code: group.name ? group.slug : null
      });
    } else {
      const proposed = this.proposeLevel('Group', v.categoryPropose ?? null, v.categoryDecision);
      if (proposed) {
        levels.push(proposed);
      }
    }
    const sub = this.subGroupParts(v.typeSlug);
    if (sub) {
      levels.push({
        label: 'Sub-group',
        name: sub.name || sub.slug,
        code: sub.name ? sub.slug : null
      });
    } else {
      const propose = v.typePropose
        ?? (v.typeDecision === 'none' && v.proposeSlug ? { slug: v.proposeSlug } : null);
      const proposed = this.proposeLevel('Sub-group', propose, v.typeDecision);
      if (proposed) {
        levels.push(proposed);
      }
    }
    return levels;
  }

  resultAction(score: ClassificationFlowEvent): string {
    const output = score.output as { outcome?: string } | null;
    const outcome = output?.outcome ?? '';
    if (outcome === 'auto_saved') {
      return 'Saved';
    }
    if (outcome === 'would_autosave' && score.predictedCategory && score.predictedType) {
      return 'Save';
    }
    if (outcome === 'error') {
      return 'Error';
    }
    return 'Leave blank';
  }

  showsPair(score: ClassificationFlowEvent): boolean {
    const output = score.output as { outcome?: string } | null;
    const outcome = output?.outcome ?? '';
    return (outcome === 'auto_saved' || outcome === 'would_autosave')
      && !!score.predictedCategory
      && !!score.predictedType;
  }

  conclusionLabel(score: ClassificationFlowEvent): string {
    const output = score.output as { outcome?: string } | null;
    const outcome = output?.outcome ?? '';
    const save = outcome === 'would_autosave' || outcome === 'auto_saved';
    const verdict = save && score.predictedCategory && score.predictedType
      ? `Save ${this.labeledCode('category', score.predictedCategory)} / ${this.labeledCode('type', score.predictedType)}`
      : 'Leave blank';
    if (!score.goldCategory) {
      return verdict;
    }
    return `${verdict} · ${this.matchLabel(score.match, true)}`;
  }

  voteTone(v: ClassificationFlowVoteRow): 'ok' | 'warn' | 'bad' {
    if (v.error) {
      return 'bad';
    }
    if (
      v.categoryDecision === 'none'
      || v.typeDecision === 'none'
      || v.categoryDecision === 'abstain'
      || v.typeDecision === 'abstain'
      || !v.categorySlug
      || !v.typeSlug
    ) {
      return 'warn';
    }
    return 'ok';
  }

  voteResultLabel(v: ClassificationFlowVoteRow): string {
    if (v.error) {
      return 'error';
    }
    if (v.categoryDecision === 'none' || v.typeDecision === 'none') {
      const bits = [
        v.categoryPropose?.slug ? this.formatPropose('category', v.categoryPropose) : '',
        v.typePropose?.slug ? this.formatPropose('type', v.typePropose) : '',
        !v.categoryPropose?.slug && !v.typePropose?.slug && v.proposeSlug
          ? `proposed ${v.proposeSlug}`
          : ''
      ].filter(Boolean);
      return bits.length > 0 ? bits.join(' · ') : 'proposed new code';
    }
    if (v.categoryDecision === 'abstain' || v.typeDecision === 'abstain') {
      return 'abstain';
    }
    if (v.categorySlug && v.typeSlug) {
      return 'vote';
    }
    return 'abstain';
  }

  matchLabel(value: boolean | null | undefined, hasGold: boolean): string {
    if (!hasGold) {
      return '—';
    }
    if (value === true) {
      return 'MATCH';
    }
    if (value === false) {
      return 'MISS';
    }
    return '—';
  }

  productImageUrl(productId: number): string {
    return this.api.productImageUrl(productId);
  }

  openImagePopup(): void {
    this.cancelCloseImagePopup();
    this.imagePopupOpen = true;
  }

  openCardImagePopup(productId: number): void {
    this.cancelCloseImagePopup();
    this.imagePopupProductId = productId;
  }

  scheduleCloseImagePopup(): void {
    this.cancelCloseImagePopup();
    this.imagePopupCloseTimer = setTimeout(() => {
      this.imagePopupOpen = false;
      this.imagePopupProductId = null;
      this.imagePopupCloseTimer = null;
    }, 180);
  }

  cancelCloseImagePopup(): void {
    if (this.imagePopupCloseTimer !== null) {
      clearTimeout(this.imagePopupCloseTimer);
      this.imagePopupCloseTimer = null;
    }
  }

  closeImagePopup(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.cancelCloseImagePopup();
    this.imagePopupOpen = false;
    this.imagePopupProductId = null;
  }

  private applyParent(phase: FlowPhase, evt: ClassificationFlowEvent): void {
    phase.event = evt;
    phase.status = evt.status;
    phase.decision = evt.decision;
    phase.why = evt.why;
    phase.model = evt.model ?? phase.model;
    phase.tokensIn = evt.tokensIn;
    phase.tokensOut = evt.tokensOut;
    phase.costUsd = evt.costUsd;
  }

  private upsertChild(phase: FlowPhase, evt: ClassificationFlowEvent): void {
    const key = `${evt.step}|${evt.model ?? ''}`;
    const idx = phase.children.findIndex((c) => `${c.step}|${c.model ?? ''}` === key);
    if (idx >= 0) {
      const next = [...phase.children];
      next[idx] = evt;
      phase.children = next;
      return;
    }
    phase.children = [...phase.children, evt];
  }

  private rollStatus(phase: FlowPhase): string {
    if (phase.children.some((c) => c.status === 'error')) return 'error';
    if (phase.children.some((c) => c.status === 'running')) return 'running';
    if (phase.children.length > 0 && phase.children.every((c) => c.status === 'done')) {
      return 'done';
    }
    return phase.status || 'running';
  }

  private upsertStep(evt: ClassificationFlowEvent): void {
    const key = `${evt.step}|${evt.model ?? ''}`;
    const idx = this.steps.findIndex((s) => `${s.step}|${s.model ?? ''}` === key);
    if (idx >= 0 && (evt.status === 'done' || evt.status === 'error')) {
      const next = [...this.steps];
      next[idx] = evt;
      this.steps = next;
      return;
    }
    if (idx >= 0 && evt.status === 'running') {
      return;
    }
    this.steps = [...this.steps, evt];
  }

  private normalizeVoteRow(raw: unknown): ClassificationFlowVoteRow | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const o = raw as Record<string, unknown>;
    const model = String(o['model'] ?? o['Model'] ?? '').trim();
    if (!model) {
      return null;
    }
    return {
      model,
      categorySlug: this.asNullableString(o['categorySlug'] ?? o['CategorySlug']),
      typeSlug: this.asNullableString(o['typeSlug'] ?? o['TypeSlug']),
      categoryDecision: this.asNullableString(o['categoryDecision'] ?? o['CategoryDecision']),
      typeDecision: this.asNullableString(o['typeDecision'] ?? o['TypeDecision']),
      proposeSlug: this.asNullableString(o['proposeSlug'] ?? o['ProposeSlug']),
      categoryPropose: this.asPropose(o['categoryPropose'] ?? o['CategoryPropose']),
      typePropose: this.asPropose(o['typePropose'] ?? o['TypePropose']),
      categoryEvidence: this.asNullableString(o['categoryEvidence'] ?? o['CategoryEvidence']),
      typeEvidence: this.asNullableString(o['typeEvidence'] ?? o['TypeEvidence']),
      why: this.asNullableString(o['why'] ?? o['Why']),
      error: this.asNullableString(o['error'] ?? o['Error']),
      tokensIn: this.asNumber(o['tokensIn'] ?? o['TokensIn']),
      tokensOut: this.asNumber(o['tokensOut'] ?? o['TokensOut']),
      costUsd: this.asNumber(o['costUsd'] ?? o['CostUsd']),
      matchGold: this.asNullableBool(o['matchGold'] ?? o['MatchGold']),
      matchCategory: this.asNullableBool(o['matchCategory'] ?? o['MatchCategory']),
      matchType: this.asNullableBool(o['matchType'] ?? o['MatchType'])
    };
  }

  private asPropose(value: unknown): ClassificationPropose | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const o = value as Record<string, unknown>;
    const propose: ClassificationPropose = {
      slug: this.asNullableString(o['slug'] ?? o['Slug']),
      nameHe: this.asNullableString(o['nameHe'] ?? o['name_he'] ?? o['NameHe']),
      nameEn: this.asNullableString(o['nameEn'] ?? o['name_en'] ?? o['NameEn']),
      why: this.asNullableString(o['why'] ?? o['Why'])
    };
    if (!propose.slug && !propose.nameHe && !propose.nameEn) {
      return null;
    }
    return propose;
  }

  private formatPropose(kind: 'category' | 'type', propose: ClassificationPropose): string {
    const slug = propose.slug || 'new';
    const name = (propose.nameHe || this.hebrewName(kind, propose.slug) || propose.nameEn || '').trim();
    return name ? `proposed ${kind} ${slug} (${this.isolate(name)})` : `proposed ${kind} ${slug}`;
  }

  /** Attach the catalog name to a group or sub-group id in an input/output panel. */
  private withCatalogName(key: string | undefined, value: string): string {
    const raw = value.trim();
    if (!raw || !this.isSlugKey(key)) {
      return value;
    }
    const kind = this.slugKind(key);
    const name = kind === 'type'
      ? this.subGroupParts(raw)?.name
      : kind === 'category'
        ? this.groupParts(raw)?.name
        : this.groupParts(raw)?.name ?? this.subGroupParts(raw)?.name;
    if (!name || name === raw) {
      return value;
    }
    return `${raw} (${this.isolate(name)})`;
  }

  private isSlugKey(key: string | undefined): boolean {
    return /slug/i.test(key || '');
  }

  private slugKind(key: string | undefined): 'category' | 'type' | 'either' {
    const normalized = (key || '').toLowerCase();
    if (normalized.includes('type') || normalized.includes('sub_group') || normalized.includes('subgroup')) {
      return 'type';
    }
    if (normalized.includes('category') || normalized.includes('group')) {
      return 'category';
    }
    return 'either';
  }

  /** English slug plus the Hebrew catalog name, when one exists. */
  labeledCode(kind: 'category' | 'type', slug: string | null | undefined): string {
    const raw = (slug || '').trim();
    if (!raw) {
      return '';
    }
    const name = this.hebrewName(kind, raw);
    return name ? `${raw} (${this.isolate(name)})` : raw;
  }

  /** Keep Hebrew inside an English slug line from pulling the slash sideways. */
  private isolate(text: string): string {
    return `\u2068${text}\u2069`;
  }

  private groupParts(
    slug: string | null | undefined
  ): { slug: string; department: string | null; name: string | null } | null {
    const raw = (slug || '').trim();
    if (!raw) {
      return null;
    }
    const nameHe = this.hebrewName('category', raw);
    if (!nameHe) {
      return { slug: raw, department: null, name: null };
    }
    const parts = nameHe.split(' › ').map((part) => part.trim()).filter((part) => part.length > 0);
    if (parts.length >= 2) {
      return {
        slug: raw,
        department: parts[0],
        name: parts.slice(1).join(' › ')
      };
    }
    return { slug: raw, department: null, name: nameHe };
  }

  private subGroupParts(
    slug: string | null | undefined
  ): { slug: string; name: string | null } | null {
    const raw = (slug || '').trim();
    if (!raw) {
      return null;
    }
    return { slug: raw, name: this.hebrewName('type', raw) };
  }

  private proposeLevel(
    label: string,
    propose: ClassificationPropose | null,
    decision: string | null | undefined
  ): TaxonomyLevel | null {
    const name = (propose?.nameHe || propose?.nameEn || '').trim();
    const slug = (propose?.slug || '').trim();
    if (name || slug) {
      return { label, name: name || 'New', code: slug || 'new' };
    }
    const decisionName = this.decisionName(decision);
    return decisionName ? { label, name: decisionName, code: null } : null;
  }

  private decisionName(decision: string | null | undefined): string | null {
    switch ((decision || '').trim().toLowerCase()) {
      case 'abstain':
        return 'Abstain';
      case 'none':
        return 'No match';
      default:
        return null;
    }
  }

  private hebrewName(kind: 'category' | 'type', slug: string | null | undefined): string | null {
    const key = (slug || '').trim().toLowerCase();
    if (!key) {
      return null;
    }
    const name = (kind === 'category'
      ? this.categoryBySlug.get(key)?.nameHe
      : this.typeBySlug.get(key)?.nameHe)?.trim();
    if (!name || name.toLowerCase() === key) {
      return null;
    }
    return name;
  }

  private async loadCodes(): Promise<void> {
    try {
      const codes = await firstValueFrom(this.api.listClassificationCodes());
      this.categoryBySlug = new Map(
        (codes.categories ?? []).map((row) => [row.slug.trim().toLowerCase(), row])
      );
      this.typeBySlug = new Map(
        (codes.types ?? []).map((row) => [row.slug.trim().toLowerCase(), row])
      );
    } catch {
      this.categoryBySlug = new Map();
      this.typeBySlug = new Map();
    }
  }

  filteredBatchCards(): ClassificationFlowBatchCard[] {
    const items = this.batch?.items ?? [];
    if (this.batchFilter === 'saved') {
      return items.filter((card) => this.cardIsSaved(card));
    }
    if (this.batchFilter === 'blank') {
      return items.filter((card) => card.outcome === 'disagreement' && !this.cardIsSaved(card));
    }
    if (this.batchFilter === 'error') {
      return items.filter((card) => card.status === 'error' || card.outcome === 'error');
    }
    return items;
  }

  cardIsSaved(card: ClassificationFlowBatchCard): boolean {
    return !!(card.appliedCategorySlug && card.appliedTypeSlug) || card.outcome === 'auto_saved';
  }

  cardPillOutcome(card: ClassificationFlowBatchCard): string {
    if (this.rerunningProductId === card.productId) {
      return 'running';
    }
    if (card.appliedCategorySlug && card.appliedTypeSlug) {
      return 'auto_saved';
    }
    return card.outcome || card.status;
  }

  cardLevels(card: ClassificationFlowBatchCard): TaxonomyLevel[] {
    const category = card.appliedCategorySlug || (card.outcome === 'auto_saved' ? card.categorySlug : null);
    const type = card.appliedTypeSlug || (card.outcome === 'auto_saved' ? card.typeSlug : null);
    if (!category || !type) {
      return [];
    }
    return this.taxonomyLevels(category, type);
  }

  batchCardAction(card: ClassificationFlowBatchCard): string {
    if (this.rerunningProductId === card.productId) {
      return 'Rerunning…';
    }
    if (this.cardIsSaved(card)) {
      return 'Saved';
    }
    if (card.outcome === 'disagreement') {
      return 'Leave blank';
    }
    if (card.status === 'error' || card.outcome === 'error') {
      return 'Error';
    }
    if (card.status === 'running') {
      return 'Running';
    }
    if (card.categoryRoute === 'aisle') {
      return 'Aisle';
    }
    if (card.categoryRoute === 'call' && card.categoryModel) {
      return `Group · ${this.modelShortName(card.categoryModel)}`;
    }
    return 'Waiting';
  }

  categoryWhy(card: ClassificationFlowBatchCard): string | null {
    if (card.outcome === 'auto_saved' || card.outcome === 'disagreement' || card.outcome === 'error' || card.status === 'error') {
      return null;
    }
    if (card.categoryRoute === 'aisle') {
      return 'Storefront aisles agree, so the group comes from the aisle.';
    }
    if (card.categoryRoute === 'call' && card.categoryModel) {
      return `Aisles do not agree, so ${this.modelShortName(card.categoryModel)} is called for the group.`;
    }
    return null;
  }

  cardCrosswalk(card: ClassificationFlowBatchCard): string | null {
    const line = card.gathered?.find((g) => g.label.toLowerCase() === 'crosswalk');
    return line?.text ?? null;
  }

  categoryPlanLine(): string | null {
    if (this.batch?.summary.route !== 'batch') {
      return null;
    }
    const phase = this.batch.summary.phase;
    const inCategory = phase === 'category_submit' || phase === 'category_wait';
    const plan = this.batch.categoryPlan;
    const aisle = plan?.aisleCount ?? 0;
    const calls = plan?.calls ?? [];
    if (!inCategory && aisle === 0 && calls.length === 0) {
      return null;
    }
    const callText = calls
      .map((row) => `${this.modelShortName(row.model)} ${row.count}`)
      .join(', ');
    const parts = [
      'Group step. The aisle is used when storefront aisles agree. A group call is made only when they do not.'
    ];
    if (aisle > 0) {
      parts.push(`${aisle} from the aisle.`);
    }
    if (callText) {
      parts.push(`Group calls: ${callText}.`);
    } else if (inCategory) {
      parts.push('No group calls yet.');
    }
    return parts.join(' ');
  }

  cardError(card: ClassificationFlowBatchCard): string | null {
    if (card.status !== 'error' && card.outcome !== 'error') {
      return null;
    }
    const voteErrors = this.cardVotes(card)
      .map((vote) => (vote.error || '').trim())
      .filter((text) => text.length > 0);
    if (voteErrors.length > 0) {
      return null;
    }
    return (this.batch?.summary.note || '').trim() || 'This product failed.';
  }

  private modelErrorText(v: ClassificationFlowVoteRow): string | null {
    const error = (v.error || '').trim();
    if (!error) {
      return null;
    }
    if (error === 'Missing from batch output.') {
      return this.missingBatchOutputText(v);
    }
    return error;
  }

  private missingBatchOutputText(v: ClassificationFlowVoteRow): string {
    const category = (v.categorySlug || '').trim();
    if (category) {
      return `The group is ${this.labeledCode('category', category)}. The sub-group was asked, but this product was not in the batch file that came back, so there is no sub-group answer.`;
    }
    return 'This product was not in the batch file that came back, so there is no answer from this model.';
  }

  cardModelRows(card: ClassificationFlowBatchCard): Array<{
    key: string;
    model: string;
    name: string;
    tone: 'ok' | 'warn' | 'bad';
    conclusion: string | null;
    levels: TaxonomyLevel[];
    times: string[];
    callSegments: ClassificationFlowCallSegment[];
    canApprove: boolean;
    saved: boolean;
  }> {
    const votes = this.cardVotes(card);
    const calls = (card.calls ?? []).filter((call) => !call.notSent);
    const models: string[] = [];
    for (const vote of votes) {
      if (!models.includes(vote.model)) {
        models.push(vote.model);
      }
    }
    for (const call of calls) {
      if (call.model && !models.includes(call.model)) {
        models.push(call.model);
      }
    }
    return models.map((model) => {
      const vote = votes.find((row) => row.model === model) ?? null;
      const category = vote ? this.approveCategorySlug(vote) : null;
      const type = vote ? this.approveTypeSlug(vote) : null;
      const saved = !!(vote && category && type && this.modelAlreadySaved(card, category, type));
      const canApprove = !!(
        vote
        && !saved
        && (card.status === 'done' || card.status === 'error')
        && !(vote.error || '').trim()
        && category
        && type
      );
      const errorText = vote ? this.modelErrorText(vote) : null;
      return {
        key: `${card.productId}|${model}`,
        model,
        name: this.modelShortName(model),
        tone: vote ? this.voteTone(vote) : 'warn',
        conclusion: errorText,
        levels: vote && !errorText ? this.voteLevels(vote) : [],
        times: calls.filter((call) => call.model === model).map((call) => this.callTimeLine(call)),
        callSegments: this.callSegments(card, model),
        canApprove,
        saved
      };
    });
  }

  async approveModel(
    card: ClassificationFlowBatchCard,
    model: string,
    event: Event
  ): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    const batchId = this.batch?.summary.id;
    const key = `${card.productId}|${model}`;
    if (!batchId || this.approvingKey) {
      return;
    }
    this.approvingKey = key;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      const result = await firstValueFrom(
        this.api.approveClassificationFlowVote(batchId, card.productId, model)
      );
      this.applyApproval(card.productId, result);
      if (result.createdType) {
        await this.loadCodes();
      }
      const category = this.labeledCode('category', result.categorySlug);
      const type = this.labeledCode('type', result.typeSlug);
      this.statusMessage = result.createdType
        ? `Added ${type} and classified this product as ${category} / ${type}.`
        : `Classified this product as ${category} / ${type}.`;
    } catch (err: unknown) {
      this.errorMessage = this.readError(err);
    } finally {
      this.approvingKey = null;
    }
  }

  async rerunCard(
    card: ClassificationFlowBatchCard,
    event: Event
  ): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    const batchId = this.batch?.summary.id;
    if (!batchId || this.rerunningProductId != null) {
      return;
    }
    this.rerunningProductId = card.productId;
    this.errorMessage = '';
    this.statusMessage = `Rerunning classification for ${card.displayName || card.barcode || card.productId}…`;
    try {
      const detail = await firstValueFrom(
        this.api.rerunClassificationFlowBatchItem(batchId, card.productId)
      );
      this.statusMessage = `Rerun completed for ${card.displayName || card.barcode || card.productId}.`;
      if (this.batch) {
        this.batch = {
          ...this.batch,
          items: this.batch.items.map((c) =>
            c.productId === card.productId
              ? {
                  ...c,
                  status: detail.status,
                  outcome: detail.outcome,
                  categorySlug: detail.categorySlug,
                  typeSlug: detail.typeSlug,
                  costUsd: detail.costUsd,
                  votes: detail.votes
                }
              : c
          )
        };
      }
      if (this.openProductId === card.productId) {
        this.batchDetail = detail;
      }
      void this.openBatch(batchId, false);
    } catch (err: unknown) {
      this.errorMessage = this.readError(err);
    } finally {
      this.rerunningProductId = null;
    }
  }

  private approveCategorySlug(vote: ClassificationFlowVoteRow): string | null {
    return this.normalizeSlug(vote.categorySlug || vote.categoryPropose?.slug);
  }

  private approveTypeSlug(vote: ClassificationFlowVoteRow): string | null {
    return this.normalizeSlug(vote.typeSlug || vote.typePropose?.slug);
  }

  private normalizeSlug(value: string | null | undefined): string | null {
    const slug = (value || '').trim().toLowerCase().replace(/ /g, '_');
    return slug || null;
  }

  private modelAlreadySaved(
    card: ClassificationFlowBatchCard,
    category: string,
    type: string
  ): boolean {
    if (card.appliedCategorySlug && card.appliedTypeSlug) {
      return card.appliedCategorySlug.toLowerCase() === category
        && card.appliedTypeSlug.toLowerCase() === type;
    }
    return card.outcome === 'auto_saved'
      && (card.categorySlug || '').toLowerCase() === category
      && (card.typeSlug || '').toLowerCase() === type;
  }

  private applyApproval(productId: number, result: ClassificationFlowVoteApproval): void {
    if (!this.batch) {
      return;
    }
    const summary = {
      ...this.batch.summary,
      savedCount: result.savedCount,
      blankCount: result.blankCount
    };
    this.batch = {
      ...this.batch,
      summary,
      items: this.batch.items.map((card) =>
        card.productId === productId
          ? {
              ...card,
              appliedCategorySlug: result.categorySlug,
              appliedTypeSlug: result.typeSlug
            }
          : card)
    };
    this.batches = this.batches.map((row) => (row.id === summary.id ? summary : row));
  }

  callSegments(card: ClassificationFlowBatchCard, model: string): ClassificationFlowCallSegment[] {
    const calls = card.calls ?? [];
    return calls
      .filter((call) => call.model === model)
      .map((call) => {
        const step = call.wave === 'group' || call.wave === 'category' ? 'Group' : 'Sub-group';
        const isAisle = call.customId.startsWith('aisle-');
        return {
          call,
          step,
          isAisle,
          notSent: call.notSent,
          sentClock: this.formatCallClock(call.sentAt),
          receivedClock: this.formatCallClock(call.receivedAt),
          answered: call.answered
        };
      });
  }

  async openCallIo(
    card: ClassificationFlowBatchCard,
    call: ClassificationFlowCallTime,
    initialTab: 'prompt' | 'output',
    event: Event
  ): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    const batchId = this.batch?.summary.id;
    if (!batchId) {
      return;
    }
    const stepTitle = call.wave === 'group' || call.wave === 'category' ? 'Group' : 'Sub-group';
    this.callIoTab = initialTab;
    this.callIoCopied = false;
    this.callIoLoading = true;
    this.callIoPopup = {
      batchId,
      customId: call.customId,
      model: call.model,
      modelName: this.modelShortName(call.model),
      wave: call.wave,
      stepTitle,
      productName: card.displayName,
      barcode: card.barcode,
      sentAt: call.sentAt,
      receivedAt: call.receivedAt,
      prompt: null,
      response: null,
      error: null,
      answered: call.answered
    };

    try {
      const detail = await firstValueFrom(
        this.api.getClassificationFlowBatchCall(batchId, call.customId)
      );
      if (this.callIoPopup && this.callIoPopup.customId === call.customId) {
        this.callIoPopup = {
          ...this.callIoPopup,
          modelName: detail.modelName || this.callIoPopup.modelName,
          sentAt: detail.sentAt || this.callIoPopup.sentAt,
          receivedAt: detail.receivedAt || this.callIoPopup.receivedAt,
          prompt: detail.prompt,
          response: detail.response,
          error: detail.error,
          productName: detail.productName || this.callIoPopup.productName,
          barcode: detail.barcode || this.callIoPopup.barcode
        };
      }
    } catch (err: unknown) {
      if (this.callIoPopup && this.callIoPopup.customId === call.customId) {
        this.callIoPopup.error = this.readError(err);
      }
    } finally {
      this.callIoLoading = false;
    }
  }

  closeCallIo(): void {
    this.callIoPopup = null;
    this.callIoLoading = false;
  }

  async copyCallIoText(text: string | null): Promise<void> {
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      this.callIoCopied = true;
      setTimeout(() => {
        this.callIoCopied = false;
      }, 2000);
    } catch {
      // Best effort clipboard
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.callIoPopup) {
      this.closeCallIo();
    }
  }

  private callTimeLine(call: ClassificationFlowCallTime): string {
    const step = call.wave === 'group' || call.wave === 'category' ? 'Group' : 'Sub-group';
    if (call.customId.startsWith('aisle-')) {
      return 'Group from the aisle.';
    }
    if (call.notSent) {
      return `${step} not sent.`;
    }
    const sent = this.formatCallClock(call.sentAt);
    const received = this.formatCallClock(call.receivedAt);
    if (sent && received) {
      return `${step} sent ${sent}, received ${received}.`;
    }
    if (sent && !call.answered) {
      return `${step} sent ${sent}, not back yet.`;
    }
    if (sent && call.answered) {
      return `${step} sent ${sent}, received, time not recorded.`;
    }
    if (received) {
      return `${step} received ${received}.`;
    }
    if (call.answered) {
      return `${step} time was not recorded.`;
    }
    return `${step} not back yet.`;
  }

  formatCallClock(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  modelShortName(model: string): string {
    switch (model) {
      case 'gemini-2.5-pro':
        return 'Gemini';
      case 'gpt-5-mini':
        return 'ChatGPT';
      case 'claude-haiku-4-5':
        return 'Claude';
      case 'deepseek-flash':
        return 'DeepSeek';
      case 'glm-4.7-flash':
        return 'GLM';
      default:
        return model;
    }
  }

  openVoteNotes(card: ClassificationFlowBatchCard): ClassificationFlowVoteRow[] {
    return this.cardVotes(card).filter(
      (vote) => !!(vote.categoryEvidence || this.showTypeEvidence(vote))
    );
  }

  showTypeEvidence(v: ClassificationFlowVoteRow): boolean {
    const typeEvidence = v.typeEvidence?.trim() ?? '';
    if (!typeEvidence) {
      return false;
    }
    return typeEvidence !== (v.categoryEvidence?.trim() ?? '');
  }

  cardVotes(card: ClassificationFlowBatchCard): ClassificationFlowVoteRow[] {
    return this.detailVotes({ votes: card.votes } as ClassificationFlowBatchItemDetail);
  }

  detailVotes(detail: ClassificationFlowBatchItemDetail): ClassificationFlowVoteRow[] {
    return (detail.votes ?? [])
      .map((raw) => this.normalizeVoteRow(raw))
      .filter((vote): vote is ClassificationFlowVoteRow => vote != null && !this.isUnsupportedBatchVote(vote));
  }

  private isUnsupportedBatchVote(vote: ClassificationFlowVoteRow): boolean {
    const error = (vote.error || '').toLowerCase();
    return error.includes('no batch api') || error.includes('was not called');
  }

  batchStepTitle(step: ClassificationFlowBatchStep): string {
    if (step.name === 'shelf_match') {
      return 'Name Match (Catalog Shelves)';
    }
    if (step.name === 'llm_consensus') {
      return 'Consensus Evaluation';
    }
    if (step.name === 'save') {
      return 'Batch Run Resolution';
    }
    const stepLabel = step.name === 'group' ? 'Group Step'
      : step.name === 'sub_group' || step.name === 'type' ? 'Sub-group Step'
      : step.name;
    const model = step.model ? this.modelShortName(step.model) : '';
    return model ? `${model} · ${stepLabel}` : stepLabel;
  }

  batchStepSubtitle(step: ClassificationFlowBatchStep): string {
    switch (step.name) {
      case 'group':
        return 'Evaluates product details against the catalog groups.';
      case 'shelf_match':
        return 'Checks if any catalog sub-group names match the product words.';
      case 'sub_group':
      case 'type':
        return 'Selects a candidate sub-group or proposes a new one if none fit.';
      case 'llm_consensus':
        return 'Evaluates agreement across voting models (requires 2 of 3 company match).';
      case 'save':
        return 'Applies write policy: auto-saves if consensus reached, otherwise leaves unlabeled.';
      default:
        return '';
    }
  }

  batchStepReason(step: ClassificationFlowBatchStep): string | null {
    if (step.why?.trim()) {
      return step.why.trim();
    }
    if (step.name === 'shelf_match') {
      return step.decision === 'none'
        ? 'No sub-groups in the catalog matched the words in the product name.'
        : null;
    }
    return null;
  }

  batchStepIoKey(productId: number, step: ClassificationFlowBatchStep, index: number): string {
    return `batch|${productId}|${step.model ?? ''}|${step.name}|${index}`;
  }

  isBatchStepIoOpen(key: string): boolean {
    return !!this.expandedIo[key];
  }

  toggleBatchStepIo(key: string): void {
    this.expandedIo = { ...this.expandedIo, [key]: !this.isBatchStepIoOpen(key) };
  }

  batchPhaseLabel(phase: string | null | undefined): string {
    switch (phase) {
      case 'category_submit':
      case 'category_wait':
        return 'Waiting for the group batch';
      case 'type_submit':
      case 'type_wait':
        return 'Waiting for the sub-group batch';
      case 'apply':
        return 'Saving results';
      case 'failed':
        return 'Batch API failed';
      default:
        return '';
    }
  }

  async startBatch(): Promise<void> {
    this.batchStarting = true;
    this.errorMessage = '';
    const limit = Math.min(100, Math.max(1, Math.round(Number(this.batchLimit) || 50)));
    this.batchLimit = limit;
    try {
      this.batch = await firstValueFrom(
        this.api.createClassificationFlowBatch(limit, this.batchApi)
      );
      this.batchFilter = 'all';
      this.openProductId = null;
      this.batchDetail = null;
      await this.loadBatches();
      this.watchBatch();
    } catch (err: unknown) {
      this.errorMessage = this.readError(err);
    } finally {
      this.batchStarting = false;
    }
  }

  async loadBatches(): Promise<void> {
    try {
      this.batches = await firstValueFrom(this.api.listClassificationFlowBatches());
      const openId = this.batch?.summary.id;
      if (openId) {
        await this.openBatch(openId, false);
      } else {
        const running = this.batches.find((row) => !row.finishedAt);
        if (running) {
          await this.openBatch(running.id, false);
        }
      }
    } catch (err: unknown) {
      this.errorMessage = this.readError(err);
    }
  }

  async openBatch(id: number, clearDetail = true): Promise<void> {
    this.errorMessage = '';
    try {
      this.batch = await firstValueFrom(this.api.getClassificationFlowBatch(id));
      const summary = this.batch.summary;
      this.batches = this.batches.map((row) => (row.id === summary.id ? summary : row));
      if (clearDetail) {
        this.openProductId = null;
        this.batchDetail = null;
      } else if (this.openProductId && !this.batchDetail && !this.batchDetailLoading) {
        const card = this.batch.items.find((item) => item.productId === this.openProductId);
        if (card && card.status !== 'pending' && card.status !== 'running') {
          void this.loadOpenDetail(id, card.productId);
        }
      }
      this.watchBatch();
    } catch (err: unknown) {
      this.errorMessage = this.readError(err);
    }
  }

  async toggleBatchCard(card: ClassificationFlowBatchCard): Promise<void> {
    if (this.openProductId === card.productId) {
      this.openProductId = null;
      this.batchDetail = null;
      return;
    }
    const batchId = this.batch?.summary.id;
    if (!batchId) {
      return;
    }
    this.openProductId = card.productId;
    this.batchDetail = null;
    if (card.status === 'pending' || card.status === 'running') {
      return;
    }
    this.batchDetailLoading = true;
    try {
      this.batchDetail = await firstValueFrom(
        this.api.getClassificationFlowBatchItem(batchId, card.productId)
      );
    } catch (err: unknown) {
      this.errorMessage = this.readError(err);
    } finally {
      this.batchDetailLoading = false;
    }
  }

  private async loadOpenDetail(batchId: number, productId: number): Promise<void> {
    this.batchDetailLoading = true;
    try {
      const detail = await firstValueFrom(this.api.getClassificationFlowBatchItem(batchId, productId));
      if (this.openProductId === productId) {
        this.batchDetail = detail;
      }
    } catch (err: unknown) {
      this.errorMessage = this.readError(err);
    } finally {
      this.batchDetailLoading = false;
    }
  }

  private watchBatch(): void {
    this.stopBatchPoll();
    if (!this.batch || this.batch.summary.finishedAt) {
      return;
    }
    const id = this.batch.summary.id;
    this.batchTimer = setInterval(() => {
      void this.openBatch(id, false);
    }, 3000);
  }

  private stopBatchPoll(): void {
    if (this.batchTimer != null) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
  }

  private readError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as { error?: string } | null;
      return body?.error || err.message || 'Request failed.';
    }
    if (err instanceof Error) {
      return err.message;
    }
    return 'Request failed.';
  }

  private asNullableString(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    const s = String(value).trim();
    return s ? s : null;
  }

  private asNumber(value: unknown): number {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  private asNullableBool(value: unknown): boolean | null {
    if (value === true || value === false) {
      return value;
    }
    return null;
  }
}
